import { z } from 'zod';
import {
  saveDraftRequestSchema,
  saveVersionRequestSchema,
  versionedContentSchema,
  versionedDetailSchema,
} from './versioning.js';

/**
 * « Méthode » (analyste.md §4, issue #196): what the analyst of the noyau knows
 * of the method - the concrete words of the three functions the first pass
 * reads, and the six styles the second pass reads - as ONE versioned object, the
 * fourth SINGLETON after the texts of the noyaux, the models and the rules, at
 * the patron of the three (`noyau-texts.ts`, `model-settings.ts`,
 * `rule-settings.ts`): immutable versions, the two pointers, a server draft,
 * « Enregistrer une nouvelle version », « Passer en production » by the
 * vitrine with no cascade.
 *
 * It is THE method, common to every protocol: nothing cites it, no reference
 * pins it, and a session freezes the version it plays only when its experience
 * plays a Noyau block (K13). It has no list, no creation, no retirement, no
 * duplication and no renaming.
 *
 * ## What a version carries (§4.2)
 *
 * Plain text, no formatting (K12), at the bounds below:
 *
 * - THE THREE FUNCTIONS, Souverain, Guerrier, Producteur: « ce qu'elle désigne »
 *   and « mots et expressions de la langue parlée » - read by the first pass;
 * - THE SIX STYLES, one per noyau of the table of §2.1: the name of the style,
 *   « ce qui le reconnaît » and the optional « mots typiques » - read by the
 *   second pass. The name, the triplet and the colour of a noyau are not in the
 *   version: they are in the table.
 *
 * Explicit keys rather than records over the enums: a Zod record over an enum
 * is PARTIAL, and every reader would branch on a function or a noyau that
 * cannot be missing - the reason `noyau-texts.ts` gives for its six keys. A
 * test holds the keys against `ANALYST_FUNCTIONS` and `NOYAU_CODES`.
 *
 * « Enregistrer une nouvelle version » demands every field NON-EMPTY but the
 * typical words of a style, and the autosaved draft absorbs a partial entry.
 * So the version's schema refuses a blank field and the draft's does not.
 *
 * It is BORN v1 of a seed (`METHOD_SEED`, `noyau/method-seed.ts`), on the
 * atelier only, and the vitrine receives it by « Passer en production »
 * (vitrine.md §5, K12).
 */

/** The identifier of the singleton entity - the one `{id}` its routes serve (K12). */
export const METHOD_ID = 'method';

/** Its name, fixed: what the layer copies onto the entity and into each version (§4.1). */
export const METHOD_NAME = 'Méthode';

/** The fixed name, as a schema: that one string, or nothing and then that one. */
const methodNameSchema = z.literal(METHOD_NAME).default(METHOD_NAME);

/** « Ce qu'elle désigne » of a function. */
export const METHOD_FUNCTION_DESIGNATES_MAX = 1000;
/** « Mots et expressions de la langue parlée » of a function. */
export const METHOD_FUNCTION_WORDS_MAX = 4000;
/** « Style »: the name of a style (Révélation, Transfert...). */
export const METHOD_STYLE_NAME_MAX = 40;
/** « Ce qui le reconnaît » of a style. */
export const METHOD_STYLE_RECOGNIZES_MAX = 2000;
/** « Mots typiques » of a style, which may stay empty. */
export const METHOD_STYLE_TYPICAL_WORDS_MAX = 2000;

/** One FUNCTION of a version: what it designates, and the words of the spoken language that carry it. */
export const methodFunctionSchema = z.object({
  designates: z.string().trim().min(1).max(METHOD_FUNCTION_DESIGNATES_MAX),
  words: z.string().trim().min(1).max(METHOD_FUNCTION_WORDS_MAX),
});
export type MethodFunction = z.infer<typeof methodFunctionSchema>;

/** One STYLE of a version: its name, what recognises it, and its typical words (facultative). */
export const methodStyleSchema = z.object({
  name: z.string().trim().min(1).max(METHOD_STYLE_NAME_MAX),
  recognizes: z.string().trim().min(1).max(METHOD_STYLE_RECOGNIZES_MAX),
  typicalWords: z.string().trim().max(METHOD_STYLE_TYPICAL_WORDS_MAX).default(''),
});
export type MethodStyle = z.infer<typeof methodStyleSchema>;

/** One function as a DRAFT holds it: anything the editor holds, bounds kept, blank included. */
export const methodFunctionDraftSchema = z.object({
  designates: z.string().trim().max(METHOD_FUNCTION_DESIGNATES_MAX).default(''),
  words: z.string().trim().max(METHOD_FUNCTION_WORDS_MAX).default(''),
});
export type MethodFunctionDraft = z.infer<typeof methodFunctionDraftSchema>;

/** One style as a DRAFT holds it. */
export const methodStyleDraftSchema = z.object({
  name: z.string().trim().max(METHOD_STYLE_NAME_MAX).default(''),
  recognizes: z.string().trim().max(METHOD_STYLE_RECOGNIZES_MAX).default(''),
  typicalWords: z.string().trim().max(METHOD_STYLE_TYPICAL_WORDS_MAX).default(''),
});
export type MethodStyleDraft = z.infer<typeof methodStyleDraftSchema>;

/** The EMPTY function and the EMPTY style, what a draft nobody has touched holds. */
export const EMPTY_METHOD_FUNCTION: MethodFunctionDraft = { designates: '', words: '' };
export const EMPTY_METHOD_STYLE: MethodStyleDraft = { name: '', recognizes: '', typicalWords: '' };

/** The three functions as explicit keys, in the order of `ANALYST_FUNCTIONS`. */
function byFunction<Entry extends z.ZodTypeAny>(entry: Entry) {
  return z.object({ S: entry, G: entry, P: entry });
}

/** The six noyaux as explicit keys, in the order of the table of `noyau.ts`. */
function byNoyau<Entry extends z.ZodTypeAny>(entry: Entry) {
  return z.object({ SGP: entry, SPG: entry, GSP: entry, GPS: entry, PGS: entry, PSG: entry });
}

/**
 * The CONTENT of one version: the three functions, the six styles, and the
 * fixed name the layer wants (§4.1).
 *
 * Not `.strict()`, like every stored shape: a field added later must not make
 * today's versions unreadable.
 */
export const methodContentSchema = z.object({
  name: methodNameSchema,
  functions: byFunction(methodFunctionSchema),
  styles: byNoyau(methodStyleSchema),
});
export type MethodContent = z.infer<typeof methodContentSchema>;

/**
 * The content of a DRAFT: every function and every style defaulted to empty
 * fields, so an autosave of one field is a legal draft. The name is the same
 * literal: a draft cannot carry a name no version could.
 */
export const methodDraftContentSchema = z.object({
  name: methodNameSchema,
  functions: byFunction(methodFunctionDraftSchema.default(EMPTY_METHOD_FUNCTION)).default({}),
  styles: byNoyau(methodStyleDraftSchema.default(EMPTY_METHOD_STYLE)).default({}),
});
export type MethodDraftContent = z.infer<typeof methodDraftContentSchema>;

/** Everything the editor of Réglages > Méthode reads in one call. */
export const methodDetailSchema = versionedDetailSchema(
  methodContentSchema,
  methodDraftContentSchema,
);
export type MethodDetail = z.infer<typeof methodDetailSchema>;

/** One version read in full, for « Voir » and « Repartir de cette version ». */
export const methodVersionSchema = versionedContentSchema(methodContentSchema);
export type MethodVersion = z.infer<typeof methodVersionSchema>;

/** « Enregistrer une nouvelle version » of Méthode. */
export const methodSaveVersionSchema = saveVersionRequestSchema(methodContentSchema);
export type MethodSaveVersion = z.infer<typeof methodSaveVersionSchema>;

/** An autosave of the draft of Méthode. */
export const methodSaveDraftSchema = saveDraftRequestSchema(methodDraftContentSchema);
export type MethodSaveDraft = z.infer<typeof methodSaveDraftSchema>;
